import React, { useState, useEffect } from 'react';
import { BookOpen, Plus, Edit3, Trash2, Save, X, ChevronDown } from 'lucide-react';
import timetableService from '../../services/timetableService';
import '../../styles/SubjectSettings.css';

const SubjectSettings = () => {
  const [subjectGroups, setSubjectGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [mode, setMode] = useState('Modify'); // Add, Modify, Delete, Cancel, Save
  const [formData, setFormData] = useState({
    subject_code: '',
    comp_code: '',
    sub_item: '00',
    subject_name_eng: '',
    subject_name_chi: '',
    subject_abbr_eng: '',
    subject_abbr_chi: '',
    subject_short_eng: '',
    subject_short_chi: '',
    component_name_eng: '',
    component_name_chi: '',
    component_short_eng: '',
    component_short_chi: ''
  });

  useEffect(() => {
    loadSubjectGroups();
  }, []);

  const loadSubjectGroups = async () => {
    try {
      const data = await timetableService.getSubjectGroups();
      setSubjectGroups(data);
      if (data.length > 0 && !selectedSubject) {
        setSelectedSubject(data[0]);
        setFormData({
          subject_code: data[0].subject_codes?.[0] || '',
          comp_code: '',
          sub_item: '00',
          subject_name_eng: data[0].subject_group_name || '',
          subject_name_chi: data[0].subject_group_name || '',
          subject_abbr_eng: data[0].subject_group_key || '',
          subject_abbr_chi: data[0].subject_group_key || '',
          subject_short_eng: data[0].subject_group_key || '',
          subject_short_chi: data[0].subject_group_key || '',
          component_name_eng: '',
          component_name_chi: '',
          component_short_eng: '',
          component_short_chi: ''
        });
      }
    } catch (error) {
      console.error('Error loading subject groups:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubjectSelect = (subject) => {
    setSelectedSubject(subject);
    setFormData({
      subject_code: subject.subject_codes?.[0] || '',
      comp_code: '',
      sub_item: '00',
      subject_name_eng: subject.subject_group_name || '',
      subject_name_chi: subject.subject_group_name || '',
      subject_abbr_eng: subject.subject_group_key || '',
      subject_abbr_chi: subject.subject_group_key || '',
      subject_short_eng: subject.subject_group_key || '',
      subject_short_chi: subject.subject_group_key || '',
      component_name_eng: '',
      component_name_chi: '',
      component_short_eng: '',
      component_short_chi: ''
    });
    setMode('Modify');
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAdd = () => {
    setMode('Add');
    setFormData({
      subject_code: '',
      comp_code: '',
      sub_item: '00',
      subject_name_eng: '',
      subject_name_chi: '',
      subject_abbr_eng: '',
      subject_abbr_chi: '',
      subject_short_eng: '',
      subject_short_chi: '',
      component_name_eng: '',
      component_name_chi: '',
      component_short_eng: '',
      component_short_chi: ''
    });
  };

  const handleModify = () => {
    setMode('Modify');
  };

  const handleDelete = async () => {
    if (selectedSubject && window.confirm('確定要刪除此科目嗎？')) {
      try {
        await timetableService.deleteSubjectGroup(selectedSubject.id);
        await loadSubjectGroups();
        setMode('Modify');
      } catch (error) {
        console.error('Error deleting subject:', error);
      }
    }
  };

  const handleCancel = () => {
    if (selectedSubject) {
      setFormData({
        subject_code: selectedSubject.subject_codes?.[0] || '',
        comp_code: '',
        sub_item: '00',
        subject_name_eng: selectedSubject.subject_group_name || '',
        subject_name_chi: selectedSubject.subject_group_name || '',
        subject_abbr_eng: selectedSubject.subject_group_key || '',
        subject_abbr_chi: selectedSubject.subject_group_key || '',
        subject_short_eng: selectedSubject.subject_group_key || '',
        subject_short_chi: selectedSubject.subject_group_key || '',
        component_name_eng: '',
        component_name_chi: '',
        component_short_eng: '',
        component_short_chi: ''
      });
    }
    setMode('Modify');
  };

  const handleSave = async () => {
    try {
      const groupData = {
        subject_group_key: formData.subject_short_eng,
        subject_group_name: formData.subject_name_eng,
        subject_codes: [formData.subject_code],
        description: `${formData.subject_name_eng} - ${formData.subject_name_chi}`
      };
      
      if (mode === 'Add') {
        await timetableService.createSubjectGroup(groupData);
      } else if (mode === 'Modify' && selectedSubject) {
        await timetableService.updateSubjectGroup(selectedSubject.id, groupData);
      }
      await loadSubjectGroups();
      setMode('Modify');
    } catch (error) {
      console.error('Error saving subject:', error);
    }
  };
  if (loading) {
    return (
      <div className="subject-settings-container">
        <div className="loading-container">
          <div className="loading-content">
            <div className="loading-spinner">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
            </div>
            <p className="loading-text">載入科目資料中...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="subject-settings-container">
      {/* Header */}
      <div className="subject-settings-header">
        <h2>Subject Items (TBM0150E)</h2>
        <p className="subtitle">科目設定 - 管理科目項目和分卷資料</p>
      </div>

      {/* Main Content */}
      <div className="subject-settings-content">
        {/* Subject Form Panel */}
        <div className="subject-form-panel">
          <h3 className="subject-form-title">Subject Items (TBM0150E)</h3>
          
          <div className="subject-form-grid">
            {/* Subject Code and Component Code */}
            <div className="subject-form-group">
              <label className="subject-form-label">Subj:</label>
              <div className="subject-input-with-button">
                <input
                  type="text"
                  value={formData.subject_code}
                  onChange={(e) => handleInputChange('subject_code', e.target.value)}
                  className="subject-form-input"
                  placeholder="047 (BIAS)"
                />
                <button type="button" className="subject-input-button">
                  <ChevronDown size={16} />
                </button>
              </div>
            </div>

            <div className="subject-form-group">
              <label className="subject-form-label">Comp. Code:</label>
              <input
                type="text"
                value={formData.comp_code}
                onChange={(e) => handleInputChange('comp_code', e.target.value)}
                className="subject-form-input"
                placeholder=""
              />
            </div>

            {/* Sub Item */}
            <div className="subject-form-group">
              <label className="subject-form-label">SubItem:</label>
              <input
                type="text"
                value={formData.sub_item}
                onChange={(e) => handleInputChange('sub_item', e.target.value)}
                className="subject-form-input"
                placeholder="00"
              />
            </div>

            {/* Subject Names */}
            <div className="subject-form-group">
              <label className="subject-form-label">Subject Name (Eng):</label>
              <input
                type="text"
                value={formData.subject_name_eng}
                onChange={(e) => handleInputChange('subject_name_eng', e.target.value)}
                className="subject-form-input"
                placeholder="Biology (AS)"
              />
            </div>

            <div className="subject-form-group">
              <label className="subject-form-label">Subject Name (Chi):</label>
              <input
                type="text"
                value={formData.subject_name_chi}
                onChange={(e) => handleInputChange('subject_name_chi', e.target.value)}
                className="subject-form-input"
                placeholder="生物(高級程度)"
              />
            </div>

            {/* Subject Abbreviated Names */}
            <div className="subject-form-group">
              <label className="subject-form-label">Subject Abbreviated Name (Eng):</label>
              <input
                type="text"
                value={formData.subject_abbr_eng}
                onChange={(e) => handleInputChange('subject_abbr_eng', e.target.value)}
                className="subject-form-input"
                placeholder="Biology(AS)"
              />
            </div>

            <div className="subject-form-group">
              <label className="subject-form-label">Subject Abbreviated Name (Chi):</label>
              <input
                type="text"
                value={formData.subject_abbr_chi}
                onChange={(e) => handleInputChange('subject_abbr_chi', e.target.value)}
                className="subject-form-input"
                placeholder="生物(高)"
              />
            </div>

            {/* Subject Short Names */}
            <div className="subject-form-group">
              <label className="subject-form-label">Subject Short Name (Eng):</label>
              <div className="subject-input-with-button">
                <input
                  type="text"
                  value={formData.subject_short_eng}
                  onChange={(e) => handleInputChange('subject_short_eng', e.target.value)}
                  className="subject-form-input"
                  placeholder="BIAS"
                />
                <button type="button" className="subject-input-button">
                  <ChevronDown size={16} />
                </button>
              </div>
            </div>

            <div className="subject-form-group">
              <label className="subject-form-label">(Chi):</label>
              <div className="subject-input-with-button">
                <input
                  type="text"
                  value={formData.subject_short_chi}
                  onChange={(e) => handleInputChange('subject_short_chi', e.target.value)}
                  className="subject-form-input"
                  placeholder="綜合"
                />
                <button type="button" className="subject-input-button">
                  <ChevronDown size={16} />
                </button>
              </div>
            </div>

            {/* Component Names */}
            <div className="subject-form-group">
              <label className="subject-form-label">Component Name (Eng):</label>
              <input
                type="text"
                value={formData.component_name_eng}
                onChange={(e) => handleInputChange('component_name_eng', e.target.value)}
                className="subject-form-input"
                placeholder=""
              />
            </div>

            <div className="subject-form-group">
              <label className="subject-form-label">Component Name (Chi):</label>
              <input
                type="text"
                value={formData.component_name_chi}
                onChange={(e) => handleInputChange('component_name_chi', e.target.value)}
                className="subject-form-input"
                placeholder=""
              />
            </div>

            {/* Component Short Names */}
            <div className="subject-form-group">
              <label className="subject-form-label">Component Short Name (Eng):</label>
              <div className="subject-input-with-button">
                <input
                  type="text"
                  value={formData.component_short_eng}
                  onChange={(e) => handleInputChange('component_short_eng', e.target.value)}
                  className="subject-form-input"
                  placeholder=""
                />
                <button type="button" className="subject-input-button">
                  <ChevronDown size={16} />
                </button>
              </div>
            </div>

            <div className="subject-form-group">
              <label className="subject-form-label">(Chi):</label>
              <div className="subject-input-with-button">
                <input
                  type="text"
                  value={formData.component_short_chi}
                  onChange={(e) => handleInputChange('component_short_chi', e.target.value)}
                  className="subject-form-input"
                  placeholder=""
                />
                <button type="button" className="subject-input-button">
                  <ChevronDown size={16} />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons Panel */}
        <div className="action-buttons-panel">
          <button
            onClick={() => window.history.back()}
            className="action-button secondary"
          >
            <X size={16} />
            Close
          </button>
          <button
            onClick={() => {/* Navigate to Associated Subject Items */}}
            className="action-button primary"
          >
            Next Page
          </button>
          <button
            onClick={handleAdd}
            className="action-button success"
          >
            <Plus size={16} />
            Add
          </button>
          <button
            onClick={handleModify}
            className="action-button primary"
          >
            <Edit3 size={16} />
            Modify
          </button>
          <button
            onClick={handleDelete}
            className="action-button danger"
          >
            <Trash2 size={16} />
            Delete
          </button>
          <button
            onClick={handleCancel}
            className="action-button secondary"
          >
            <X size={16} />
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="action-button success"
          >
            <Save size={16} />
            Save
          </button>
        </div>
      </div>

      {/* Subject List Panel */}
      <div className="subject-list-panel">
        <div className="subject-list-header">
          <h3 className="subject-list-title">Current Subject Items:</h3>
          <span className="subject-list-count">{subjectGroups.length}</span>
        </div>
        
        <div className="subject-list">
          {subjectGroups.map((subject, index) => (
            <div
              key={subject.id}
              onClick={() => handleSubjectSelect(subject)}
              className={`subject-item ${selectedSubject?.id === subject.id ? 'selected' : ''}`}
            >
              <div className="subject-item-code">
                {subject.subject_codes?.[0] || '000'}-----{subject.subject_group_key}-----...
              </div>
              <div className="subject-item-details">
                Subject Code: {subject.subject_codes?.[0] || '000'} | Key: {subject.subject_group_key} | Name: {subject.subject_group_name}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Mode Indicator */}
      <div className="mode-indicator">
        Mode: {mode}
      </div>

      {/* Instructions */}
      <div className="instructions-section">
        <h4 className="instructions-title">科目設定指引</h4>
        <div className="instructions-content">
          <p><strong>1. 核對科目資料：</strong> 如發覺畫面中缺少了一些需用的科目，用者須回到學年過渡策劃修正科目代碼資料後，重新進行納取資料。</p>
          <p><strong>2. 系統如發現索引名稱(Key)相同：</strong> 會將後來的索引名稱改為以「~」符號為首的字串，用者可按「編修」(Modify)鍵進行修改。</p>
          <p><strong>3. 用者亦能在本功能增加、修改或删除科目分卷資料：</strong> 用者可將原有的科目改為第一分卷，然後再增加其他科目分卷。</p>
          <p><strong>4. 輸入完成後，按「儲存」(Save)鍵紀錄資料：</strong> 按「下頁」(Next Page)鍵編修相連的科目資料。</p>
        </div>
      </div>
    </div>
  );
};

export default SubjectSettings;
